import {defineSlotRecipe} from '@chakra-ui/react3'
import {LEVEL1_COLUMN_FLEX} from '../../components/navigation/flyout/partials/flyout-navigation.recipe'

/**
 * The share the reserved column takes, by the level standing in for it.
 *
 * Level 1 is a fixed 15% of the row; level 2 takes whatever is left, the same
 * `flex: 1` its placement box carries in the flyout. Read by the `spacer`
 * slot's own `reserve` variant, so the two cannot drift.
 */
const RESERVE_FLEX_MAP = {
    level1: LEVEL1_COLUMN_FLEX,
    level2: 1
}

/** What the reserved column stands for, named so an editor can read it. */
export const RESERVE_LABEL_MAP = {
    level1: 'L1',
    level2: 'L2'
}

/**
 * Layout for the scaffolding the preview adds around the panes, which keep the
 * flyout's own slots.
 *
 * slots:
 *  - stack: the level-2 module's column — its own pane, then each level-3 pane
 *  - spacer: the reserved column, sized by which level it stands in for
 *  - paneItem: the flex item a pane needs when the spacer comes before it
 *  - toolNav: the tool-nav row's placement, over the frame's padding box
 *
 * variants:
 *  - reserve (spacer): level1 | level2
 *  - desktopOnly (toolNav): true | false
 */
export const previewNavigationRecipe = defineSlotRecipe({
    className: 'navigation-preview',
    slots: ['stack', 'spacer', 'paneItem', 'toolNav'],
    base: {
        stack: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-40'
        },
        spacer: {
            // desktop-only: mobile shows one pane at a time and the flyout
            // parks the others out of flow, so there is no column to stand in
            // for
            display: {base: 'none', s: 'flex'},
            minWidth: 0,
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingBlockStart: 'spacing-8',
            // editor chrome, drawn outside the design system so it cannot be
            // read as a component the design specifies
            border: '2px dashed',
            borderColor: 'border.subtle',
            fontSize: '10px',
            lineHeight: 1,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            opacity: 0.6
        },
        paneItem: {
            flex: {s: 1},
            minWidth: 0
        },
        toolNav: {
            // desktop pins the row to the frame's padding box, which is inside
            // the frame's own inline padding; mobile keeps it in the flow, where
            // the padding already applies and a non-zero inset would shift it
            // instead
            insetInline: {s: 'var(--preview-frame-inline-padding)'}
        }
    },
    variants: {
        reserve: {
            level1: {spacer: {flex: {s: RESERVE_FLEX_MAP.level1}}},
            level2: {spacer: {flex: {s: RESERVE_FLEX_MAP.level2}}}
        },
        desktopOnly: {
            true: {toolNav: {display: {base: 'none', s: 'block'}}},
            false: {}
        }
    },
    defaultVariants: {
        desktopOnly: false
    }
})

export default previewNavigationRecipe
